import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FilterBar } from "@/components/ui";
import { SearchField } from "@/components/ui";
import { cn } from "@/lib/utils";

export function buildListResetToken(parts: unknown[]): string {
  return parts.map((part) => String(part ?? "")).join("|");
}

export interface ListToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  searchAccessory?: ReactNode;
  filterChildren?: ReactNode;
  className?: string;
}

export function ListToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  searchAccessory,
  filterChildren,
  className,
}: ListToolbarProps) {
  const { t } = useTranslation("common");

  const resolvedPlaceholder = searchPlaceholder ?? t("search");

  const searchField = showSearch ? (
    <SearchField
      value={search}
      onChange={onSearchChange}
      placeholder={resolvedPlaceholder}
      searchLabel={t("search")}
      clearSearchLabel={t("clearSearch")}
    />
  ) : null;

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {filterChildren ? (
        <FilterBar>{filterChildren}</FilterBar>
      ) : null}
      {showSearch && searchAccessory ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <SearchField
            value={search}
            onChange={onSearchChange}
            placeholder={resolvedPlaceholder}
            className="flex-1"
            searchLabel={t("search")}
            clearSearchLabel={t("clearSearch")}
          />
          {searchAccessory}
        </div>
      ) : (
        searchField
      )}
    </div>
  );
}

export interface AdminListToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchAccessory?: ReactNode;
  filterChildren: ReactNode;
  className?: string;
}

export function AdminListToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  searchAccessory,
  filterChildren,
  className,
}: AdminListToolbarProps) {
  const { t } = useTranslation("common");
  const resolvedPlaceholder = searchPlaceholder ?? t("search");

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {searchAccessory ? (
        <div className="flex flex-wrap items-end gap-3">
          <SearchField
            value={search}
            onChange={onSearchChange}
            placeholder={resolvedPlaceholder}
            className="min-w-[240px] max-w-none flex-1"
            searchLabel={t("search")}
            clearSearchLabel={t("clearSearch")}
          />
          {searchAccessory}
        </div>
      ) : (
        <SearchField
          value={search}
          onChange={onSearchChange}
          placeholder={resolvedPlaceholder}
          searchLabel={t("search")}
          clearSearchLabel={t("clearSearch")}
        />
      )}
      <FilterBar>{filterChildren}</FilterBar>
    </div>
  );
}
